import { SharedQuizItemV2 } from '@/features/Quiz/components/SharedQuizItemV2';
import React, { useCallback, useEffect, ChangeEvent } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/redux/store';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Table,
    Tbody,
    Td,
    Thead,
    Tr,
    Spinner as SpinnerChakra,
    Button,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { HiX } from 'react-icons/hi';
import * as yup from 'yup';
import { debounce } from '@/utils/helpers';
import { Pagination } from '@/components/Pagination';
import { ThEl } from '@/features/Quiz/components/ArticlesQuiz';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
    getAllQuizzesSharedByAdminV2,
    resetQuizSharedByAdminV2Pages,
    selectQuizSharedByAdminV2ActivePage,
    selectQuizSharedByAdminV2Loading,
    selectQuizSharedByAdminV2Page,
    selectQuizSharedByAdminV2TotalPages,
    setQuizSharedByAdminV2ActivePage,
} from '@/redux/reducers/quizzes/quizSharedByAdminV2';
import Icon from '@/components/Icon';

const searchInputSchema = yup.object().shape({
    keyword: yup.string(),
});

type SearchFormData = {
    keyword: string;
};

export const SharedQuizV2 = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useAuth();
    const isSuperAdmin = user?.roles.includes('ROLE_SUPER_ADMIN');

    const activePage = useSelector(selectQuizSharedByAdminV2ActivePage, shallowEqual);
    const totalPages = useSelector(selectQuizSharedByAdminV2TotalPages, shallowEqual);
    const rows = useSelector((state: RootState) => selectQuizSharedByAdminV2Page(state, activePage), shallowEqual);
    const isLoading = useSelector(selectQuizSharedByAdminV2Loading, shallowEqual);

    const {
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting },
        watch,
        getValues,
        setValue,
    } = useForm<SearchFormData>({
        resolver: yupResolver(searchInputSchema),
        defaultValues: {
            keyword: '',
        },
    });

    const values = getValues();

    const onSearchKeywordSubmit = useCallback(
        (formValues: SearchFormData) => {
            dispatch(resetQuizSharedByAdminV2Pages());
            dispatch(setQuizSharedByAdminV2ActivePage(1));
            dispatch(
                getAllQuizzesSharedByAdminV2({
                    activePage: 1,
                    activeSearchKeyword: formValues.keyword,
                })
            );
        },
        [dispatch]
    );

    const onSearchKeywordChange = debounce(
        useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                setValue('keyword', e.target.value);
                dispatch(resetQuizSharedByAdminV2Pages());
                dispatch(setQuizSharedByAdminV2ActivePage(1));
                dispatch(
                    getAllQuizzesSharedByAdminV2({
                        activePage: 1,
                        activeSearchKeyword: e.target.value ?? '',
                    })
                );
            },
            [dispatch, setValue]
        ),
        300
    );

    const handleReset = useCallback(() => {
        reset();
        dispatch(
            getAllQuizzesSharedByAdminV2({
                activePage: 1,
                activeSearchKeyword: '',
            })
        );
    }, [dispatch, reset]);

    const handlePaginationChange = useCallback(
        (page: number) => {
            dispatch(setQuizSharedByAdminV2ActivePage(page));
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(
            getAllQuizzesSharedByAdminV2({
                activePage,
                activeSearchKeyword: values.keyword ?? '',
            })
        );
    }, [dispatch, activePage]);

    useEffect(
        () => () => {
            dispatch(setQuizSharedByAdminV2ActivePage(1));
        },
        [dispatch]
    );

    if (!isSuperAdmin) {
        return <Navigate to="/quiz/my-quiz" replace />;
    }

    return (
        <Flex w="100%" flexDir="column" mb={'10px'}>
            <Flex gap="20px" sx={{ '.btn-half-width': { flex: '1 0 45%' } }} w="100%">
                <Flex justifyContent={'space-between'} alignItems={'center'} mb={3} w="100%">
                    <form onSubmit={handleSubmit(onSearchKeywordSubmit)}>
                        <InputGroup minW={'400px'} className="input-left-wrap">
                            <InputLeftElement
                                className="InputLeft"
                                pointerEvents={isSubmitting ? 'none' : 'auto'}
                                onClick={handleSubmit(onSearchKeywordSubmit)}>
                                {isSubmitting || isLoading ? <SpinnerChakra size="20px" /> : <FaSearch size="20px" />}
                            </InputLeftElement>
                            <Input
                                placeholder="Search Admin Quiz by Title"
                                bg={'#ffffff'}
                                id={'keyword-shared-v2'}
                                type={'text'}
                                borderRadius="1000px"
                                {...register('keyword')}
                                onChange={onSearchKeywordChange}
                            />
                            {watch('keyword') && (
                                <InputRightElement cursor="pointer" onClick={handleReset}>
                                    <HiX />
                                </InputRightElement>
                            )}
                        </InputGroup>
                    </form>
                    <Button as={Link} to={'/quiz/admin-quiz-v2/create'} variant={'brand'}>
                        <Icon icon="plus" />
                        &nbsp; Create a New Quiz
                    </Button>
                </Flex>
            </Flex>
            <Flex flexDir={'column'}>
                <Table bgColor={'#ffffff'}>
                    {rows.length > 0 && (
                        <Thead borderBottom="1px solid #e5ebf3">
                            <Tr w="100%">
                                <ThEl align="start" w="45%">
                                    Title
                                </ThEl>
                                <ThEl align="start" w="45%">
                                    Shared with
                                </ThEl>
                                <ThEl w="10%" align="start">
                                    Date Created
                                </ThEl>
                                <ThEl w="15%">Questions</ThEl>
                                <ThEl w="15%">Deadline</ThEl>
                                <ThEl w="15%">Actions</ThEl>
                            </Tr>
                        </Thead>
                    )}
                    <Tbody>
                        {rows.length > 0 ? (
                            rows.map((row) => <SharedQuizItemV2 key={`${row.quizId}-${row.sharedWith}-${row.sharedAt ?? ''}`} row={row} />)
                        ) : (
                            <>
                                {!isLoading && (
                                    <Tr>
                                        <Td>
                                            <Box>No list items</Box>
                                        </Td>
                                    </Tr>
                                )}
                            </>
                        )}
                    </Tbody>
                </Table>

                {totalPages > 1 && (
                    <Flex justifyContent={'end'} mt={'20px'}>
                        <Pagination currentPage={activePage} totalPages={totalPages} handlePagination={handlePaginationChange} />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
